import { EvaluationReviewStatus } from '@shared/models/assessmentdetails';
import {
  BrainFunctionEvaluationRating,
  EVALUATIONS_COMPLETENESS,
} from '@shared/constants/assessments';

enum DOMAIN_NAMES {
  SENSORY = 'Sensory',
  MOTOR = 'Motor',
  SOCIAL = 'Social',
}

enum ASSESSMENT_DOMAINS {
  SEEING_READING = 'SeeingReading',
  HEARING_UNDERSTANDING = 'HearingUnderstanding',
  SENSATION_TACTILITY = 'SensationTactility',
  LOCOMOTION_MOBILITY = 'LocomotionSpeech',
  COMMUNICATION_SPEECH = 'CommunicationSpeech',
  MANUAL_WRITING = 'ManualWriting',
  EMOTIONAL_SOCIAL = 'EmotionalSocial',
}

interface IBrainLevel {
  brainLevelId: number;
  age: number;
  name: string; // it maybe change e.g: "MEDULLA OBLONGATA"
  ageRange: string; // it maybe change e.g: "BIRTH to 0.5 MONTHS"
}

export interface IBrainFunction {
  id: number;
  name: string;
  domainDescription: string;
  category: DOMAIN_NAMES;
  domain: ASSESSMENT_DOMAINS;
  level: IBrainLevel;
}

export interface IAssessmentMatrix {
  brainFunctions: IBrainFunction[];
  brainLevels: IBrainLevel[];
}

interface IFunctionEvaluation {
  brainFunctionId: number;
  evaluationRating: BrainFunctionEvaluationRating;
  prevEvaluationRating?: BrainFunctionEvaluationRating;
  reviewStatus: EvaluationReviewStatus | null;
  prevAssessmentNotesExist?: boolean;
}

interface ILevelEvaluation {
  levelId: number;
  functionsEvaluations: IFunctionEvaluation[];
  markedAsNotYetAchievedBulk: boolean;
}

export interface IAssessmentEvaluationResponseData {
  brainDomain: ASSESSMENT_DOMAINS;
  functionalAgeAtLevel8: number;
  levelEvaluations: ILevelEvaluation[];
}

export enum BRAIN_DOMAIN_LEVEL {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
  SIX = 6,
  SEVEN = 7,
  EIGHT = 8,
}

export type AssessmentEvaluationMatrix = Record<string, IEvaluationMatrixData>;

interface IEvaluationMatrixData {
  domain: ASSESSMENT_DOMAINS;
  levels: IEvaluationMatrixLevelData[];
}

interface IEvaluationMatrixLevelData {
  level: BRAIN_DOMAIN_LEVEL;
  rating: EVALUATIONS_COMPLETENESS;
  requiredReview: boolean;
  completion: number;
}

export class AssessmentMatrix {
  constructor(public matrix: IAssessmentMatrix) {}

  public get isEmpty() {
    return !this.matrix.brainFunctions.length && !this.matrix.brainLevels.length;
  }

  public get Levels() {
    return this.matrix.brainLevels.map(
      level => new BrainLevel(level.brainLevelId, level.name, level.ageRange, level.age),
    );
  }

  public get CategoriesAndDomains() {
    return this.matrix.brainFunctions.reduce(
      (result: BrainCategoryDomain[], current: IBrainFunction) => {
        const existingElement = result.find(item => item.category === current.category);

        if (existingElement) {
          const existingDomain = existingElement.domains
            .map(item => item.id)
            .includes(current.domain);
          if (!existingDomain) {
            existingElement.domains.push({ id: current.domain, name: current.domainDescription });
          }
        } else {
          result.push(
            new BrainCategoryDomain(current.category, [
              { id: current.domain, name: current.domainDescription },
            ]),
          );
        }

        return result;
      },
      [],
    );
  }

  public get Domains() {
    return this.CategoriesAndDomains.map(item => item.domains).flat(1);
  }
}

class BrainLevel {
  constructor(
    public brainLevelId: number,
    public name: string,
    public ageRange: string,
    public age: number,
  ) {}
}

class Domain {
  constructor(
    public id: string,
    public name: string,
  ) {}
}

class BrainCategoryDomain {
  constructor(
    public category: string,
    public domains: Domain[],
  ) {}
}
